import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PTicketService } from '../../../services/p-ticket.service';
import { differences } from '../../../interfaces/loggingDifferences';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { pTicket } from '../../../interfaces/pTicket';
import { MatTabsModule } from '@angular/material/tabs';
import { DescriptionsHistoryViewComponent } from '../../descriptions-history-view/descriptions-history-view.component';
import { NgIf, CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { PDescriptionService } from '../../../services/p-description.service';
import { pDescription } from '../../../interfaces/pDescription';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PTicketEditTicketDataComponent } from '../p-ticket-edit-ticket-data/p-ticket-edit-ticket-data.component';
import { PTicketEditUserDataComponent } from '../p-ticket-edit-user-data/p-ticket-edit-user-data.component';
import { openSnackBar } from '../../../helpers/genericFunctions';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatMenuModule } from '@angular/material/menu';
import { PKeywordPickerComponent } from '../../p-keyword-picker/p-keyword-picker.component';
import { MatDialog } from '@angular/material/dialog';
import { DescriptionNewComponent } from '../../description-new/description-new.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HeaderTitleService } from '../../../services/header-title.service';
import { OrganizationService } from '../../../services/organization.service';
import { globalUser } from '../../../helpers/globalFields';

/**
 * Detailed view for one pTicket. Contains all of the tickets data, user data and pDescriptions
 */
@Component({
  selector: 'app-p-ticket-detail',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatTabsModule,
    DescriptionsHistoryViewComponent,
    NgIf,
    MatButtonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatListModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatToolbarModule,
    PTicketEditTicketDataComponent,
    PTicketEditUserDataComponent,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatGridListModule,
    MatTooltipModule,
    MatMenuModule,
    PKeywordPickerComponent,
    MatProgressSpinner,
    MatTooltipModule,
  ],
  templateUrl: './p-ticket-detail.component.html',
  styleUrl: './p-ticket-detail.component.scss',
})
export class PTicketDetailComponent {
  /**
   * constructor
   *
   * @param route fetches the ticket id from the route so that the correct ticket can be loaded
   * @param pTicketService service to update/fetch ticket data
   * @param pDescriptionService service to update/fetch pdescription data
   * @param router angular routing
   * @param snackBar
   */
  constructor(
    private route: ActivatedRoute,
    private pTicketService: PTicketService,
    private pDescriptionService: PDescriptionService,
    private headerTitle: HeaderTitleService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    pTicketService.pTicketChanged.subscribe(() => this.getPTicket());
  }

  /**
   * current pTicket
   */
  pTicket!: pTicket;
  /**
   * list of all pDescriptions belonging to one ticket
   */
  pDescriptions: pDescription[] = [];
  /**
   * message that will be commited upon change of pTicket
   */
  message = {
    pTicketID: 1,
    pde_description: 'Info changed \n',
    pde_type: 'ChatBot',
  };

  canDeleteTicket: boolean = false;

  protected globalUser = globalUser;

  /**
   * if a pDescription is added, this emitter fires and triggers a page relaod
   */
  @Output() pDescriptionAddedEmitter = new EventEmitter();

  @ViewChild(PTicketEditTicketDataComponent)
  pTicketEditTicketDataComponent!: PTicketEditTicketDataComponent;

  @ViewChild(PTicketEditUserDataComponent)
  pTicketEditUserDataComponent!: PTicketEditUserDataComponent;

  isReopeningTicket: boolean = false;
  isClosingTicket: boolean = false;
  isUpdatingTicket: boolean = false;

  ticketUpdatePermission: boolean = false;
  /**
   * intially gets the pTicket upon loading of the page
   */
  ngOnInit(): void {
    this.getPTicket();
    globalUser.getUserStream().subscribe((user) => {
      if (user) {
        this.ticketUpdatePermission = globalUser.validateTicketPermission('ticket.update', this.pTicket);
        this.canDeleteTicket = globalUser.validateTicketPermission('ticket.delete', this.pTicket);
      }
    });
  }

  onDeleteTicket(): void {
    this.pTicketService.setInactive(this.pTicket.id).subscribe(response => {
      this.router.navigate(['/pTickets/list']);
    })
  }


  onAddDescription(): void {
    const dialogRef = this.dialog.open(DescriptionNewComponent, {
      data: { ticket: this.pTicket, descriptions: this.pDescriptions },
      width: '100%',
      maxWidth: '800px',
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result: pTicket | undefined) => {
      this.getPTicket();
    });
  }

  onUpdateTicketInfo(): void {
    this.isUpdatingTicket = true;
    this.pTicketEditTicketDataComponent.onTicketDataUpdate();
  }

  onUpdateUserInfo(): void {
    this.isUpdatingTicket = true;
    this.pTicketEditUserDataComponent.onUserDataUpdate();
  }

  /**
   * advances the support level.
   */
  onNextSupport(): void {
    let success_message = 'Support level updated';
    let error_message = 'Error while updating the support level ';
    // find differences that should be logged into the ticket
    let differences: differences[] = [
      {
        key: 'pct_supportlevel',
        value1: this.pTicket!.pct_supportlevel,
        value2: (+this.pTicket!.pct_supportlevel + 1).toString(),
      },
    ];

    if (+this.pTicket!.pct_supportlevel == 3) {
      openSnackBar('Maximum support level reached', 2000, this.snackBar);
    } else {
      this.pTicketService
        .updatePTicket(
          this.pTicket.id,
          {
            pct_supportlevel: (+this.pTicket!.pct_supportlevel + 1).toString(),
          },
          differences,
          success_message,
          error_message,
        )
        .subscribe(() => {});
    }
  }

  /**
   * closes the ticket
   */
  onCloseTicket(): void {
    this.isClosingTicket = true;
    let success_message = `Ticket ${this.pTicket.id} closed`;
    let error_message = `Error while closing ticket ${this.pTicket.id}`;
    // find differences that should be logged into the ticket
    let differences: differences[] = [
      {
        key: 'pct_status',
        value1: 'open',
        value2: 'closed',
      },
    ];
    this.pTicketService
      .updatePTicket(
        this.pTicket.id,
        { pct_status: 'closed' },
        differences,
        success_message,
        error_message,
      )
      .subscribe((result) => {
        this.isClosingTicket = false;
        this.getPTicket();
      });
  }

  /**
   * reopens the ticket
   */

  onReopenTicket(): void {
    // setup messaging
    this.isReopeningTicket = true;
    let success_message = `Ticket ${this.pTicket.id} reopened`;
    let error_message = `Error while reopening pTicket ${this.pTicket.id}`;
    // find differences that should be logged into the ticket
    let differences: differences[] = [
      { key: 'pct_status', value1: 'closed', value2: 'open' },
    ];
    console.log('reopen');
    this.pTicketService
      .updatePTicket(
        this.pTicket.id,
        { pct_status: 'open' },
        differences,
        success_message,
        error_message,
      )
      .subscribe(() => {
        this.isReopeningTicket = false;
        this.pTicketEditTicketDataComponent.pTicketOpen();
        this.pTicketEditUserDataComponent.pTicketOpen();
        this.getPTicket();
      });
  }

  /**
   * reloads the ticket upon update
   * @param msg unused msg from the event
   */
  pDescriptionAddedEventFired(msg: any) {
    this.getPTicket();

    // update entries
    this.pTicketEditTicketDataComponent.fillForm();
    this.pTicketEditUserDataComponent.fillForm();

    this.isUpdatingTicket = false;
  }

  /**
   * gets current pTicket data from api
   */
  getPTicket(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.pTicketService.getTicket(id).subscribe(
      (pTicket) => {
        this.pTicket = pTicket;
        this.headerTitle.setTitle(+pTicket.id + ' | ' + pTicket.pct_user);

        this.getPDescriptions();
        this.ticketUpdatePermission = globalUser.validateTicketPermission('ticket.update', this.pTicket);
        this.canDeleteTicket = globalUser.validateTicketPermission('ticket.delete', this.pTicket);
      },
      (error) => {
        openSnackBar("Can't get pTicket data" + error, 5000, this.snackBar);
      },
    );
  }

  

  /**
   * gets all pDescriptions by pTicket id
   */
  getPDescriptions() {
    this.pDescriptionService
      .getByPTicketID(this.pTicket!.id)
      .subscribe((pDescriptions) => {
        this.pDescriptions = pDescriptions;
      });
  }
}
